/**
 * Billing API Service
 *
 * Handles all billing-related API calls including balance retrieval,
 * transaction history, and credit management.
 *
 * All requests include credentials: 'include' for cookie-based session management
 * and CSRF tokens for state-changing operations.
 */

import { getCsrfToken } from './authApi';
import type {
  BalanceTransaction,
  BalanceTransactionType,
  TransactionListOptions,
  DepositBundle,
  PaymentInvoice,
  InvoiceStatus,
} from '../../../shared/types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

/**
 * Custom error class for billing API errors
 */
export class BillingApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string
  ) {
    super(message);
    this.name = 'BillingApiError';
  }
}

/**
 * Callback for handling 401 responses
 * Set by AuthContext to clear auth state and redirect to login
 */
let onUnauthorized: (() => void) | null = null;

/**
 * Register a callback to handle 401 Unauthorized responses
 */
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

/**
 * Handle API response and extract JSON or throw appropriate error
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    // Handle 401 Unauthorized - session expired or invalid
    if (response.status === 401) {
      if (onUnauthorized) {
        onUnauthorized();
      }
      throw new BillingApiError(response.status, 'Session expired. Please log in again.');
    }

    const body = await response.json().catch(() => ({ error: { message: 'An error occurred' } }));
    // Error response format: { success: false, error: { message: "...", code: "..." } }
    const errorData = body.error || body;
    throw new BillingApiError(
      response.status,
      errorData.message || 'An error occurred',
      errorData.code
    );
  }
  return response.json();
}

/**
 * Get headers for API requests
 * Includes CSRF token for state-changing methods (POST, PATCH, DELETE)
 */
function getHeaders(method: string = 'GET'): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  // Include CSRF token for state-changing requests
  const csrfMethods = ['POST', 'PATCH', 'PUT', 'DELETE'];
  if (csrfMethods.includes(method.toUpperCase())) {
    const token = getCsrfToken();
    if (token) {
      headers['X-CSRF-Token'] = token;
    }
  }

  return headers;
}

/**
 * Build request options with credentials and optional signal
 */
function buildRequestOptions(
  method: string,
  body?: unknown,
  signal?: AbortSignal
): RequestInit {
  const options: RequestInit = {
    method,
    headers: getHeaders(method),
    credentials: 'include',
  };

  if (body !== undefined) {
    options.body = JSON.stringify(body);
  }

  if (signal) {
    options.signal = signal;
  }

  return options;
}

/**
 * Balance response from API
 */
export interface BalanceResponse {
  success: boolean;
  balance: number;
  /** Available credits (same as balance, for backwards compatibility) */
  available?: number;
  /** Credits reserved for active generations */
  reserved?: number;
  /** Total credits including reserved */
  total?: number;
  companyId: string;
  companyName?: string;
}

/**
 * Transaction list response from API
 */
export interface TransactionsResponse {
  success: boolean;
  transactions: BalanceTransaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Add credits request body
 */
export interface AddCreditsRequest {
  amount: number;
  description?: string;
}

/**
 * Billing API methods
 */
export const billingApi = {
  /**
   * Get balance for current user's company
   */
  async getBalance(signal?: AbortSignal): Promise<BalanceResponse> {
    const response = await fetch(
      `${API_BASE}/billing/balance`,
      buildRequestOptions('GET', undefined, signal)
    );
    // Server returns { success, data: { balance, available, reserved, total } }
    const result = await handleResponse<{
      success: boolean;
      data: { balance: number; available?: number; reserved?: number; total?: number };
    }>(response);
    return {
      success: result.success,
      balance: result.data?.balance ?? 0,
      available: result.data?.available ?? result.data?.balance ?? 0,
      reserved: result.data?.reserved ?? 0,
      total: result.data?.total ?? result.data?.balance ?? 0,
      companyId: '',
    };
  },

  /**
   * Get balance for a specific company (admin only)
   */
  async getBalanceForCompany(
    companyId: string,
    signal?: AbortSignal
  ): Promise<BalanceResponse> {
    const response = await fetch(
      `${API_BASE}/billing/balance/${encodeURIComponent(companyId)}`,
      buildRequestOptions('GET', undefined, signal)
    );
    // Server returns { success, data: { companyId, balance } }
    const result = await handleResponse<{ success: boolean; data: { companyId: string; balance: number } }>(response);
    return {
      success: result.success,
      balance: result.data?.balance ?? 0,
      companyId: result.data?.companyId ?? companyId,
    };
  },

  /**
   * Get transaction history with optional filters
   */
  async getTransactions(
    options?: TransactionListOptions & { signal?: AbortSignal }
  ): Promise<TransactionsResponse> {
    const params = new URLSearchParams();

    if (options?.companyId) {
      params.set('companyId', options.companyId);
    }
    if (options?.type) {
      params.set('type', options.type);
    }
    if (options?.fromDate) {
      params.set('fromDate', options.fromDate);
    }
    if (options?.toDate) {
      params.set('toDate', options.toDate);
    }
    if (options?.limit !== undefined) {
      params.set('limit', String(options.limit));
    }
    if (options?.offset !== undefined) {
      params.set('offset', String(options.offset));
    }

    const queryString = params.toString();
    const url = `${API_BASE}/billing/transactions${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(
      url,
      buildRequestOptions('GET', undefined, options?.signal)
    );
    // Server returns { success, data, pagination } - transform to { success, transactions, pagination }
    const result = await handleResponse<{ success: boolean; data: BalanceTransaction[]; pagination: TransactionsResponse['pagination'] }>(response);
    return {
      success: result.success,
      transactions: result.data || [],
      pagination: result.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 },
    };
  },

  /**
   * Add credits to a company (admin only)
   */
  async addCredits(
    companyId: string,
    amount: number,
    description?: string,
    signal?: AbortSignal
  ): Promise<{ success: boolean; transaction: BalanceTransaction; newBalance: number }> {
    const body = {
      companyId,
      amount,
      description,
    };

    const response = await fetch(
      `${API_BASE}/billing/credits`,
      buildRequestOptions('POST', body, signal)
    );
    // Server returns { success, data: transaction }
    const result = await handleResponse<{ success: boolean; data: BalanceTransaction }>(response);
    return {
      success: result.success,
      transaction: result.data,
      newBalance: result.data?.balanceAfter ?? 0,
    };
  },

  /**
   * Get transaction types (for filtering)
   */
  getTransactionTypes(): BalanceTransactionType[] {
    return ['CREDIT', 'HOLD', 'CHARGE', 'RELEASE', 'REFUND', 'ADJUSTMENT'];
  },

  // =========================================================================
  // Deposit Functions
  // =========================================================================

  /**
   * Get active deposit bundles
   */
  async getActiveBundles(signal?: AbortSignal): Promise<{ bundles: DepositBundle[] }> {
    const response = await fetch(
      `${API_BASE}/billing/bundles`,
      buildRequestOptions('GET', undefined, signal)
    );
    // Server returns { success, data: bundles[] }
    const result = await handleResponse<{ success: boolean; data: DepositBundle[] }>(response);
    return { bundles: result.data || [] };
  },

  /**
   * Create a deposit invoice
   */
  async createDeposit(
    bundleId: string,
    signal?: AbortSignal
  ): Promise<{ invoice: PaymentInvoice }> {
    const response = await fetch(
      `${API_BASE}/billing/deposit`,
      buildRequestOptions('POST', { bundleId }, signal)
    );
    // Server returns { success, data: invoice }
    const result = await handleResponse<{ success: boolean; data: PaymentInvoice }>(response);
    return { invoice: result.data };
  },

  /**
   * Submit transaction hash for an invoice
   */
  async submitTxHash(
    invoiceId: string,
    txHash: string,
    signal?: AbortSignal
  ): Promise<{ invoice: PaymentInvoice }> {
    const response = await fetch(
      `${API_BASE}/billing/invoices/${encodeURIComponent(invoiceId)}/submit-tx`,
      buildRequestOptions('POST', { txHash }, signal)
    );
    // Server returns { success, data: invoice }
    const result = await handleResponse<{ success: boolean; data: PaymentInvoice }>(response);
    return { invoice: result.data };
  },

  /**
   * Cancel an invoice
   */
  async cancelInvoice(
    invoiceId: string,
    reason?: string,
    signal?: AbortSignal
  ): Promise<{ invoice: PaymentInvoice }> {
    const response = await fetch(
      `${API_BASE}/billing/invoices/${encodeURIComponent(invoiceId)}/cancel`,
      buildRequestOptions('POST', { reason }, signal)
    );
    // Server returns { success, data: invoice }
    const result = await handleResponse<{ success: boolean; data: PaymentInvoice }>(response);
    return { invoice: result.data };
  },

  /**
   * Confirm an invoice (admin only)
   * Requires admin password for verification
   */
  async confirmInvoice(
    invoiceId: string,
    adminPassword: string,
    amountReceivedUsdt?: number,
    signal?: AbortSignal
  ): Promise<{ invoice: PaymentInvoice }> {
    const response = await fetch(
      `${API_BASE}/billing/invoices/${encodeURIComponent(invoiceId)}/confirm`,
      buildRequestOptions('POST', { adminPassword, amountReceivedUsdt }, signal)
    );
    // Server returns { success, data: invoice }
    const result = await handleResponse<{ success: boolean; data: PaymentInvoice }>(response);
    return { invoice: result.data };
  },

  /**
   * Get invoice by ID
   */
  async getInvoice(
    invoiceId: string,
    signal?: AbortSignal
  ): Promise<{ invoice: PaymentInvoice }> {
    const response = await fetch(
      `${API_BASE}/billing/invoices/${encodeURIComponent(invoiceId)}`,
      buildRequestOptions('GET', undefined, signal)
    );
    // Server returns { success, data: invoice }
    const result = await handleResponse<{ success: boolean; data: PaymentInvoice }>(response);
    return { invoice: result.data };
  },

  /**
   * List invoices for current company
   */
  async listInvoices(
    options?: {
      status?: InvoiceStatus;
      limit?: number;
      offset?: number;
      signal?: AbortSignal;
    }
  ): Promise<{
    invoices: PaymentInvoice[];
    pagination: { page: number; limit: number; total: number; totalPages: number };
  }> {
    const params = new URLSearchParams();
    if (options?.status) params.set('status', options.status);
    if (options?.limit !== undefined) params.set('limit', String(options.limit));
    if (options?.offset !== undefined) params.set('offset', String(options.offset));

    const queryString = params.toString();
    const url = `${API_BASE}/billing/invoices${queryString ? `?${queryString}` : ''}`;

    const response = await fetch(
      url,
      buildRequestOptions('GET', undefined, options?.signal)
    );
    // Server returns { success, data: invoices[], pagination }
    const result = await handleResponse<{
      success: boolean;
      data: PaymentInvoice[];
      pagination: { page: number; limit: number; total: number; totalPages: number };
    }>(response);
    return {
      invoices: result.data || [],
      pagination: result.pagination || { page: 1, limit: 50, total: 0, totalPages: 0 },
    };
  },
};

export default billingApi;

export type { DepositBundle, PaymentInvoice, InvoiceStatus };
